import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import useCollectionHexagonTooltip from '../hooks/useCollectionHexagonTooltip';

/**
 * Renders the hover-tooltip DOM node for collection hexagon cells.
 *
 * Same imperative-element + portal approach as CollectionTooltip: OpenLayers
 * relocates the overlay element into its own container, so React must not own
 * that element directly (it would throw "removeChild" on unmount). We create the
 * element once, hand it to the hook for OL attachment, and portal React content
 * into it.
 *
 * @param {object} props
 * @param {boolean} props.enabled - gate (the hexagon layer's visibility)
 * @param {import('ol/layer/WebGLVector').default|null} props.layer - the hexagon layer
 * @param {import('../utils/collectionHexagonsToCells').HexagonCell[]} props.cells
 */
const CollectionHexagonTooltip = ({ enabled, layer, cells }) => {
	const overlayElRef = useRef(null);
	if (!overlayElRef.current) {
		overlayElRef.current = document.createElement('div');
		overlayElRef.current.className = 'ol-tooltip';
	}

	const { tooltipContent } = useCollectionHexagonTooltip(enabled, layer, cells, overlayElRef);

	// Defensive detach on unmount (the hook removes the overlay itself).
	useEffect(() => {
		const el = overlayElRef.current;
		return () => el?.remove();
	}, []);

	return createPortal(tooltipContent, overlayElRef.current);
};

export default CollectionHexagonTooltip;
